import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Menu, X, User } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/upload', label: 'Analyze' },
    { path: '/marketplace', label: 'Marketplace' },
    { path: '/consultants', label: 'Consultants' },
    { path: '/pricing', label: 'Pricing' },
  ];

  return (
    <header className="fixed top-0 left-0 right-0 z-50 h-20 bg-white/95 backdrop-blur-md border-b border-[#EBE6DD] flex items-center justify-between px-6 md:px-12 transition-all">
      {/* Logo */}
      <Link to="/" className="flex items-center gap-2 group">
        <img
          src="/logo.jpg"
          alt="Vastuspace.AI logo"
          className="w-9 h-9 object-contain rounded-full border border-[#826239]/20 transition-transform group-hover:scale-105"
        />
        <span className="text-xl font-bold tracking-tight text-[#352520]">
          Vastuspace<span className="text-[#D97706]">.AI</span>
        </span>
      </Link>

      {/* Desktop Navigation */}
      <nav className="hidden md:flex items-center gap-8">
        {navLinks.map((link) => {
          const isActive = location.pathname === link.path;
          return (
            <Link
              key={link.path}
              to={link.path}
              className={`text-sm font-medium tracking-wide transition-colors ${
                isActive ? 'text-[#D97706]' : 'text-[#5D4E37] hover:text-[#352520]'
              }`}
            >
              {link.label}
            </Link>
          );
        })}
      </nav>

      {/* Actions */}
      <div className="hidden md:flex items-center gap-6">
        {user ? (
          <div className="flex items-center gap-4">
            <Link to="/dashboard" className="text-sm font-medium text-[#5D4E37] hover:text-[#352520] flex items-center gap-1">
              <User className="w-4 h-4" />
              <span>{user.name}</span>
            </Link>
            <button
              onClick={handleLogout}
              className="text-xs font-semibold uppercase tracking-wider text-[#B8342C] hover:text-red-700 transition-colors"
            >
              Logout
            </button>
          </div>
        ) : (
          <Link
            to="/login"
            className="text-sm font-medium tracking-wide text-[#5D4E37] hover:text-[#352520] transition-colors"
          >
            Login
          </Link>
        )}
        <Link
          to="/ai-assistant"
          className="bg-[#FFF8F2] border border-[#D97706] hover:bg-[#C25E03] hover:border-[#C25E03] hover:text-white text-[#D97706] rounded-full px-6 py-2.5 text-xs font-semibold tracking-widest transition-all duration-300 uppercase"
        >
          Ask Assistant
        </Link>
      </div>

      {/* Mobile Menu Button */}
      <button
        className="md:hidden p-2 text-[#352520]"
        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
      >
        {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
      </button>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden absolute top-20 left-0 right-0 bg-white border-b border-[#EBE6DD] shadow-lg animate-fadeIn">
          <nav className="flex flex-col p-6 gap-4">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className="text-sm font-medium text-[#5D4E37] hover:text-[#352520] transition-colors py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                {link.label}
              </Link>
            ))}
            <hr className="border-[#EBE6DD] my-2" />
            <div className="flex flex-col gap-4">
              {user ? (
                <>
                  <div className="flex items-center gap-2 text-sm text-[#5D4E37]">
                    <User className="w-4 h-4" />
                    <span>Logged in as {user.name}</span>
                  </div>
                  <Link
                    to="/dashboard"
                    className="text-sm font-medium text-[#5D4E37] hover:text-[#352520]"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Dashboard
                  </Link>
                  <button onClick={handleLogout} className="text-left text-sm font-medium text-[#B8342C]">
                    Logout
                  </button>
                </>
              ) : (
                <Link
                  to="/login"
                  className="text-sm font-medium text-[#5D4E37] hover:text-[#352520]"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Login
                </Link>
              )}
              <Link
                to="/ai-assistant"
                className="bg-[#FFF8F2] border border-[#D97706] hover:bg-[#C25E03] hover:border-[#C25E03] hover:text-white text-[#D97706] text-center rounded-full py-3 text-xs font-semibold tracking-widest transition-all uppercase"
                onClick={() => setMobileMenuOpen(false)}
              >
                Ask Assistant
              </Link>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
